import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    makeStyles,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import { ApartmentContractType } from '../../models/Company/types'

const useStyles = makeStyles((theme: Theme) => ({
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        color: theme.palette.primary.main,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        paddingTop: theme.spacing(1),
    },
    actions: {
        padding: theme.spacing(2, 3),
        gap: theme.spacing(1),
    },
}))

export interface ContractUploadChoice {
    category: ApartmentContractType
    organizationContractTypeId: number | null
}

export interface ContractTypeOption {
    id: number
    name: string
    is_required_for_current_company?: boolean
}

const CATEGORY_LABELS: Record<ApartmentContractType, string> = {
    [ApartmentContractType.CERTIFICATE]: 'Certificates',
    [ApartmentContractType.TURN]: 'Turn Vendors',
    [ApartmentContractType.GENERAL_VENDOR]: 'General Vendor',
}

export interface ContractTypeChoiceDialogProps {
    open: boolean
    onClose: () => void
    fileCount: number
    onChoose: (choice: ContractUploadChoice) => void
    contractTypes?: ContractTypeOption[]
    /** When set, files are filed in this category and the picker is hidden. */
    lockedCategory?: ApartmentContractType
    title?: string
    catalogLabel?: string
}

export const ContractTypeChoiceDialog: React.FC<ContractTypeChoiceDialogProps> = ({
    open,
    onClose,
    fileCount,
    onChoose,
    contractTypes = [],
    lockedCategory,
    title = 'Upload contracts',
    catalogLabel = 'Contract type',
}) => {
    const classes = useStyles()
    const [category, setCategory] = useState<ApartmentContractType>(
        ApartmentContractType.GENERAL_VENDOR,
    )
    const [
        organizationContractTypeId,
        setOrganizationContractTypeId,
    ] = useState<number | ''>('')

    useEffect(() => {
        if (open) {
            setCategory(lockedCategory ?? ApartmentContractType.GENERAL_VENDOR)
            setOrganizationContractTypeId('')
        }
    }, [open, lockedCategory])

    const selectedCategory = lockedCategory ?? category

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography>
                <Box className={classes.titleRow}>
                    <DescriptionIcon className={classes.titleIcon} />
                    <Typography variant="h6" component="span">
                        {title}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent className={classes.body}>
                <Typography variant="body2" color="textSecondary">
                    {fileCount === 1
                        ? `Choose how this ${
                              catalogLabel === 'Certificate type'
                                  ? 'certificate'
                                  : 'contract'
                          } should be filed before it is analyzed.`
                        : `Choose how these ${fileCount} ${
                              catalogLabel === 'Certificate type'
                                  ? 'certificates'
                                  : 'contracts'
                          } should be filed before they are analyzed.`}
                </Typography>
                <TextField
                    select
                    label={catalogLabel}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={organizationContractTypeId}
                    onChange={(e) =>
                        setOrganizationContractTypeId(
                            e.target.value === '' ? '' : Number(e.target.value),
                        )
                    }
                    helperText={
                        catalogLabel === 'Certificate type'
                            ? 'The organization certificate catalog. Untyped certificates still count in the library.'
                            : 'The organization catalog. Untyped contracts still count in the library.'
                    }
                >
                    <MenuItem value="">Untyped</MenuItem>
                    {contractTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                            {type.name}
                            {type.is_required_for_current_company
                                ? ' (required)'
                                : ''}
                        </MenuItem>
                    ))}
                </TextField>
                {lockedCategory ? (
                    <Typography variant="body2">
                        These files will be saved under{' '}
                        <strong>{CATEGORY_LABELS[lockedCategory]}</strong>.
                    </Typography>
                ) : (
                    <Box>
                        <Typography variant="subtitle2">
                            Contract category
                        </Typography>
                        <RadioGroup
                            value={category}
                            onChange={(e) =>
                                setCategory(
                                    e.target.value as ApartmentContractType,
                                )
                            }
                        >
                            <FormControlLabel
                                value={ApartmentContractType.CERTIFICATE}
                                control={<Radio color="primary" size="small" />}
                                label="Certificates — licenses, permits, and certificates"
                            />
                            <FormControlLabel
                                value={ApartmentContractType.TURN}
                                control={<Radio color="primary" size="small" />}
                                label="Turn Vendors — make-ready work between residents"
                            />
                            <FormControlLabel
                                value={ApartmentContractType.GENERAL_VENDOR}
                                control={<Radio color="primary" size="small" />}
                                label="General Vendor — ongoing service agreements"
                            />
                        </RadioGroup>
                    </Box>
                )}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button onClick={onClose} color="default" variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={() =>
                        onChoose({
                            category: selectedCategory,
                            organizationContractTypeId:
                                organizationContractTypeId === ''
                                    ? null
                                    : organizationContractTypeId,
                        })
                    }
                    color="primary"
                    variant="contained"
                >
                    Upload
                </Button>
            </DialogActions>
        </Dialog>
    )
}
